import { useState } from 'react';
import { api, billingAdminApi, type AdminInvoice, type AdminInvoiceEmail, type BillingSettingsView } from '../../api/client';
import { Button, Card, Chip, Dialog, Drawer, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import type { Tone } from '../../lib/format';
import { useData } from '../../lib/useData';
import { money } from '../tenant/Billing';
import { BillingTaxes } from './BillingTaxes';

/**
 * Stitch Admin → Billing (1.13.0, Teampace 3099): the month's run (preview, hold or let go, issue),
 * every company's invoices with their emails, and for one invoice its PDF and usage file, its emails
 * and Send email, a void (before anything is paid) or a credit note. And the billing settings:
 * whether companies see Billing in their portal and invoice emails to customers (both off until
 * billing goes live), and Ceexo's details on invoices. Invoice
 * emails that bounced or were marked as spam (Resend's webhook) are flagged on each, and listed.
 */

const day = (d: string | null | undefined) => {
  if (!d) return '';
  const [y, m, dd] = d.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
const monthName = (d: string) => {
  const [y, m] = d.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};
/** This month as "2026-11", in Eastern Time (billing's calendar). */
function thisMonth(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit' }).format(new Date());
}
function nextMonth(m: string): string {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, mm, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function stateOf(i: AdminInvoice): { label: string; tone: Tone } {
  if (i.status === 'draft') return { label: 'Draft', tone: 'neutral' };
  if (i.status === 'held') return { label: `Held${i.heldByName ? ` by ${i.heldByName}` : ''}`, tone: 'warn' };
  if (i.voided) return { label: 'Void', tone: 'neutral' };
  if (i.paidCents >= i.dueCents) return { label: 'Paid', tone: 'ok' };
  return { label: 'Open', tone: 'neutral' };
}

function emailsOf(i: AdminInvoice): string {
  const e = i.emails;
  if (!e) return '';
  if (e.sent) return `Sent to ${e.sent}${e.failed ? `, ${e.failed} failed` : ''}${e.bounced ? `, ${e.bounced} bounced` : ''}`;
  if (e.failed) return `${e.failed} failed`;
  if (e.skipped) return 'Not sent';
  return 'Waiting';
}

export function AdminBilling() {
  const [month, setMonth] = useState(thisMonth());
  const [open, setOpen] = useState<string | null>(null);
  const customers = useData(() => api.customers(), [], { live: false });
  const names = new Map((customers.data?.customers ?? []).map((c) => [c.id, c.name] as const));
  return (
    <div className="content billing">
      <PageHeader eyebrow="Stitch Admin" title="Billing" />
      <BillingSettings />
      <EmailProblems onOpen={setOpen} />
      <Run month={month} setMonth={setMonth} names={names} onOpen={setOpen} />
      <Invoices month={month} onOpen={setOpen} />
      <BillingTaxes month={month} />
      {open && <InvoiceDrawer id={open} names={names} onClose={() => setOpen(null)} />}
    </div>
  );
}

function BillingSettings() {
  const s = useData(() => billingAdminApi.settings(), [], { live: false });
  const [draft, setDraft] = useState<{ issuerAddress: string; issuerEmail: string; issuerPhone: string } | null>(null);
  const [error, setError] = useState('');
  if (s.error) return <LoadError error={s.error} retry={s.reload} />;
  if (!s.data) return <Loading />;
  const v: BillingSettingsView = s.data.settings;
  const bounces = s.data.bounces;
  const d = draft ?? { issuerAddress: v.issuer.address, issuerEmail: v.issuer.email, issuerPhone: v.issuer.phone };
  const save = async (body: Parameters<typeof billingAdminApi.saveSettings>[0]) => {
    setError('');
    try {
      await billingAdminApi.saveSettings(body);
      setDraft(null);
      s.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <Card
      title="What customers get, and Ceexo’s details"
      action={
        <span className="billing-actions">
          <Chip tone={v.customersSeeBilling ? 'ok' : 'warn'} small>
            {v.customersSeeBilling ? 'Customers see Billing' : 'Billing hidden from customers'}
          </Chip>
          <Chip tone={v.sendInvoiceEmails ? 'ok' : 'warn'} small>
            {v.sendInvoiceEmails ? 'Emails to customers on' : 'Emails to customers off'}
          </Chip>
        </span>
      }
    >
      <h3 className="section-title">Customers see Billing</h3>
      <p className="muted">
        Companies see Billing (invoices, plans, payment methods) in their portal. Leave off until billing goes live.{' '}
        {v.customersSeeBilling
          ? 'On: their admins, and roles with Billing, have it in their menu.'
          : 'Off: their menus have no Billing, and a bookkeeper sees “Billing in Stitch starts soon”. You still see it with Open their portal.'}
      </p>
      <div className="billing-actions">
        <Button variant={v.customersSeeBilling ? 'secondary' : 'primary'} onClick={() => void save({ customersSeeBilling: !v.customersSeeBilling })}>
          {v.customersSeeBilling ? 'Hide Billing from customers' : 'Show Billing to customers'}
        </Button>
      </div>
      <h3 className="section-title">Invoice emails</h3>
      <p className="muted">
        {v.sendInvoiceEmails
          ? 'Each invoice is emailed to its company’s billing contacts with its PDF as soon as it’s issued.'
          : 'Off until billing goes live, so the shadow run emails nobody. Invoices are still made, with their PDFs; you can send one by hand.'}
        {!v.customersSeeBilling && ' While customers don’t see Billing, an invoice email has no View invoice link: it asks them to reply with any questions.'}
      </p>
      <div className="billing-actions">
        <Button variant={v.sendInvoiceEmails ? 'secondary' : 'primary'} onClick={() => void save({ sendInvoiceEmails: !v.sendInvoiceEmails })}>
          {v.sendInvoiceEmails ? 'Turn invoice emails off' : 'Turn invoice emails on'}
        </Button>
      </div>
      {bounces && (
        <p className="muted small">
          {bounces.on ? (
            <>
              Bounces and spam complaints come from Resend’s webhook at <code>{bounces.url}</code> (events email.bounced, email.complained and email.suppressed).
            </>
          ) : (
            <>
              Bounces aren’t flagged yet: in Resend add a webhook to <code>{bounces.url}</code> for email.bounced, email.complained and email.suppressed, and set its signing secret as
              RESEND_WEBHOOK_SECRET on the portal.
            </>
          )}
        </p>
      )}
      <h3 className="section-title">Ceexo’s details on invoices</h3>
      <div className="billing-fields">
        <Field label="Ceexo’s address on invoices" value={d.issuerAddress} onChange={(e) => setDraft({ ...d, issuerAddress: e.target.value })} />
        <Field label="Billing email" type="email" value={d.issuerEmail} onChange={(e) => setDraft({ ...d, issuerEmail: e.target.value })} hint="Replies to invoice emails go here" />
        <Field label="Billing phone" value={d.issuerPhone} onChange={(e) => setDraft({ ...d, issuerPhone: e.target.value })} />
      </div>
      {draft && (
        <div className="billing-actions">
          <span className="grow" />
          <Button variant="ghost" onClick={() => setDraft(null)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save(d)}>
            Save details
          </Button>
        </div>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </Card>
  );
}

function Run({ month, setMonth, names, onOpen }: { month: string; setMonth: (m: string) => void; names: Map<string, string>; onOpen: (id: string) => void }) {
  const r = useData(() => billingAdminApi.run(month), [month], { live: false });
  const [busy, setBusy] = useState('');
  const [said, setSaid] = useState('');
  const [error, setError] = useState('');
  const act = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    setError('');
    setSaid('');
    try {
      const out = await fn();
      if (what === 'issue' && out && typeof out === 'object') {
        const o = out as { issued: number; held: number; already: number; waiting: number };
        setSaid(`Issued ${o.issued}. ${o.held ? `${o.held} held. ` : ''}${o.waiting ? `${o.waiting} waiting for usage. ` : ''}${o.already ? `${o.already} already issued.` : ''}`);
      }
      r.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  };
  const invoices = (r.data?.invoices ?? []).filter((i) => i.kind !== 'first');
  const drafts = invoices.filter((i) => i.status !== 'issued');
  return (
    <Card
      title={`The ${monthName(`${month}-01`)} run`}
      action={
        <span className="billing-actions">
          <Button variant="ghost" size="small" onClick={() => setMonth(prevMonth(month))}>
            Earlier
          </Button>
          <Button variant="ghost" size="small" onClick={() => setMonth(nextMonth(month))}>
            Later
          </Button>
        </span>
      }
    >
      <p className="muted">Preview makes every company’s invoice for the month as a draft, with last month’s beside it. Hold any to check; Issue sends the rest (on or after the 1st).</p>
      <div className="billing-actions">
        <Button disabled={Boolean(busy)} onClick={() => void act('preview', () => billingAdminApi.preview(month))}>
          {busy === 'preview' ? 'Making drafts…' : 'Preview'}
        </Button>
        <Button variant="primary" disabled={Boolean(busy) || drafts.length === 0} onClick={() => void act('issue', () => billingAdminApi.issue(month))}>
          {busy === 'issue' ? 'Issuing…' : `Issue ${drafts.filter((i) => i.status === 'draft').length}`}
        </Button>
      </div>
      {said && <Notice tone="ok">{said}</Notice>}
      {error && <Notice tone="crit">{error}</Notice>}
      {r.error ? (
        <LoadError error={r.error} retry={r.reload} />
      ) : !r.data ? (
        <Loading />
      ) : invoices.length === 0 ? (
        <Empty title="No invoices for this month yet">Preview makes them.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Invoice</th>
                <th className="num">Total</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td>{names.get(i.customerId) ?? i.customerId}</td>
                  <td>
                    <button type="button" className="link-btn mono" onClick={() => onOpen(i.id)}>
                      {i.number ?? `${i.kind === 'final' ? 'Final' : 'Monthly'} draft`}
                    </button>
                  </td>
                  <td className="num">{money(i.totalCents)}</td>
                  <td>
                    <Chip tone={stateOf(i).tone} small>
                      {stateOf(i).label}
                    </Chip>
                  </td>
                  <td className="num">
                    {i.status !== 'issued' && (
                      <Button size="small" variant="ghost" disabled={Boolean(busy)} onClick={() => void act('hold', () => billingAdminApi.hold(i.id, i.status !== 'held'))}>
                        {i.status === 'held' ? 'Let it go' : 'Hold'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function emailWord(e: AdminInvoiceEmail): string {
  if (e.problem === 'bounced') return 'Bounced';
  if (e.problem === 'complained') return 'Marked as spam';
  return e.status === 'sent' ? 'Sent' : e.status === 'failed' ? 'Failed' : 'Not sent';
}

function emailTone(e: AdminInvoiceEmail): Tone {
  if (e.problem || e.status === 'failed') return 'crit';
  return e.status === 'sent' ? 'ok' : 'neutral';
}

/**
 * Invoice emails that bounced or were marked as spam (Resend's webhook, 3099), newest first. Shown only
 * when there are some: fix the company's billing contacts, then send the invoice again.
 */
function EmailProblems({ onOpen }: { onOpen: (id: string) => void }) {
  const d = useData(() => billingAdminApi.emailProblems(), [], { live: false });
  if (d.error) return <LoadError error={d.error} retry={d.reload} />;
  if (!d.data || d.data.problems.length === 0) return null;
  return (
    <Card
      title="Invoice emails that bounced"
      action={
        <Chip tone="crit" small>
          {d.data.problems.length}
        </Chip>
      }
    >
      <p className="muted">
        Fix the company’s billing contacts (Who gets invoices, on its Billing page), then open the invoice here and choose Send email. Resend refuses an address that bounced until it’s taken off its
        suppression list.
      </p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Address</th>
              <th>Invoice</th>
              <th>What happened</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {d.data.problems.map((p) => (
              <tr key={p.id}>
                <td>{p.company}</td>
                <td>{p.to}</td>
                <td>
                  <button type="button" className="link-btn mono" onClick={() => onOpen(p.invoiceId)}>
                    {p.number ?? p.invoiceId}
                  </button>
                </td>
                <td>
                  <Chip tone="crit" small>
                    {p.problem === 'bounced' ? 'Bounced' : 'Marked as spam'}
                  </Chip>{' '}
                  <span className="muted small">{p.problemDetail}</span>
                </td>
                <td className="muted">{p.problemAt ? new Date(p.problemAt).toLocaleString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function prevMonth(m: string): string {
  const [y, mm] = m.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, mm - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function Invoices({ month, onOpen }: { month: string; onOpen: (id: string) => void }) {
  const [all, setAll] = useState(false);
  const d = useData(() => billingAdminApi.invoices(all ? undefined : month), [month, all], { live: false });
  return (
    <Card
      title={all ? 'Every issued invoice' : `Issued for ${monthName(`${month}-01`)}`}
      action={
        <Button size="small" variant="ghost" onClick={() => setAll(!all)}>
          {all ? 'Only this month' : 'Every month'}
        </Button>
      }
    >
      {d.error ? (
        <LoadError error={d.error} retry={d.reload} />
      ) : !d.data ? (
        <Loading />
      ) : d.data.invoices.length === 0 ? (
        <Empty title="No invoices issued yet" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Company</th>
                <th>Issued</th>
                <th>Due</th>
                <th className="num">Amount due</th>
                <th>Status</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {d.data.invoices.map((i) => (
                <tr key={i.id}>
                  <td>
                    <button type="button" className="link-btn mono" onClick={() => onOpen(i.id)}>
                      {i.number}
                    </button>
                  </td>
                  <td>{i.company}</td>
                  <td>{day(i.issuedOn)}</td>
                  <td>{day(i.dueOn)}</td>
                  <td className="num">{money(i.dueCents)}</td>
                  <td>
                    <Chip tone={stateOf(i).tone} small>
                      {stateOf(i).label}
                    </Chip>
                  </td>
                  <td className={i.emails?.failed || i.emails?.bounced ? 'crit-text' : 'muted'}>{emailsOf(i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function InvoiceDrawer({ id, names, onClose }: { id: string; names: Map<string, string>; onClose: () => void }) {
  const inv = useData(() => billingAdminApi.invoice(id), [id], { live: false });
  const em = useData(() => billingAdminApi.emails(id), [id], { live: false });
  const [ask, setAsk] = useState<'void' | 'credit' | null>(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [said, setSaid] = useState('');
  const i = inv.data?.invoice;
  const issued = i?.status === 'issued';
  const act = async (fn: () => Promise<unknown>, done: string) => {
    setError('');
    setSaid('');
    try {
      await fn();
      setSaid(done);
      setAsk(null);
      setReason('');
      setAmount('');
      inv.reload();
      em.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <Drawer open onClose={onClose} title={i?.number ? `Invoice ${i.number}` : 'Draft invoice'} subtitle={i ? `${names.get(i.customerId) ?? i.customerId} · ${monthName(i.period)}` : ''}>
      {inv.error ? (
        <LoadError error={inv.error} retry={inv.reload} />
      ) : !i ? (
        <Loading />
      ) : (
        <div className="stack">
          <dl className="billing-kv">
            {(i.snapshot?.sections ?? []).map((s) => (
              <div key={s.id}>
                <dt>{s.title}</dt>
                <dd>{money(s.totalCents)}</dd>
              </div>
            ))}
            <div>
              <dt>
                <strong>Amount due</strong>
              </dt>
              <dd>
                <strong>{money(i.dueCents)}</strong>
              </dd>
            </div>
          </dl>
          <p className="muted small">
            {issued ? `Issued ${day(i.issuedOn)}, due ${day(i.dueOn)}.` : 'A draft: its PDF is made when it’s issued.'} {i.paidCents > 0 ? `${money(i.paidCents)} paid.` : ''}
            {i.voided ? ` Voided by ${i.voided.byName}: ${i.voided.reason}.` : ''}
          </p>
          {issued && (
            <div className="billing-actions">
              <a className="btn secondary small" href={billingAdminApi.fileUrl(i.id, 'pdf')} download>
                PDF
              </a>
              <a className="btn secondary small" href={billingAdminApi.fileUrl(i.id, 'csv')} download>
                Usage file
              </a>
              <span className="grow" />
              <Button size="small" onClick={() => void act(() => billingAdminApi.send(i.id), 'Sent to the company’s billing contacts.')}>
                Send email
              </Button>
            </div>
          )}
          {issued && !i.voided && (
            <div className="billing-actions">
              {i.paidCents === 0 && (
                <Button size="small" variant="danger" onClick={() => setAsk('void')}>
                  Void
                </Button>
              )}
              <Button size="small" variant="ghost" onClick={() => setAsk('credit')}>
                Credit note
              </Button>
            </div>
          )}
          {said && <Notice tone="ok">{said}</Notice>}
          {error && <Notice tone="crit">{error}</Notice>}
          {/* The Teampace link (3090, 3093) adds the invoice's sync status here. */}
          {issued && (
            <Card title="Emails">
              {em.error ? (
                <LoadError error={em.error} retry={em.reload} />
              ) : !em.data ? (
                <Loading />
              ) : em.data.emails.length === 0 ? (
                <p className="muted">Not yet: the email follows within a few minutes of issuing.</p>
              ) : (
                <ul className="plain-list">
                  {em.data.emails.map((e) => (
                    <li key={e.id}>
                      <Chip tone={emailTone(e)} small>
                        {emailWord(e)}
                      </Chip>{' '}
                      {e.to || e.detail} <span className="muted small">{new Date(e.at).toLocaleString()}</span>
                      {e.to && e.detail && <div className="muted small">{e.detail}</div>}
                      {e.problem && (
                        <div className="crit-text small">
                          {e.problemDetail || (e.problem === 'bounced' ? 'Bounced.' : 'Marked as spam.')}
                          {e.problemAt ? ` (${new Date(e.problemAt).toLocaleString()})` : ''}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      )}
      {ask && i && (
        <Dialog
          open
          onClose={() => setAsk(null)}
          title={ask === 'void' ? `Void ${i.number}` : `Credit note on ${i.number}`}
          subtitle={
            ask === 'void'
              ? 'Only before anything is paid. Its days are billed on the next invoice, and any credit it used goes back to the account.'
              : 'Fees and taxes come off in the same proportion; the total becomes account credit for the next invoice.'
          }
          foot={
            <>
              <span className="grow" />
              <Button variant="ghost" onClick={() => setAsk(null)}>
                Cancel
              </Button>
              <Button
                variant={ask === 'void' ? 'danger' : 'primary'}
                disabled={reason.trim().length < 3 || (ask === 'credit' && !(Number(amount) > 0))}
                onClick={() =>
                  void act(
                    () => (ask === 'void' ? billingAdminApi.void(i.id, reason.trim()) : billingAdminApi.creditNote(i.id, Math.round(Number(amount) * 100), reason.trim())),
                    ask === 'void' ? 'Voided.' : 'Credit note issued.'
                  )
                }
              >
                {ask === 'void' ? 'Void invoice' : 'Issue credit note'}
              </Button>
            </>
          }
        >
          {ask === 'credit' && <Field label="Charges to credit ($, before fees and tax)" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="10.00" />}
          <Field label="Reason (the company sees it)" value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
        </Dialog>
      )}
    </Drawer>
  );
}
